import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type { PromocodeErrorCode } from '@/types/promocode';
import { redeemPromocodeHandler } from '~/handlers/redeem-promocode.handler';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { GAMIFICATION_SLUGS } from '~/types/player-profile';

export type PromocodeNotificationType = 'success' | 'error' | 'warning';

// 'ready' = o formulário de resgate. 'ineligible' = conta abaixo do nível CRM
// mínimo da gamificação (`gamifications.promocodes.is_eligible: false` em
// /players/me): sobe o modal de aviso de nível no lugar do formulário, do mesmo
// jeito que a roletinha e o check-in fazem.
//
// Não há 'loading' nem 'error' aqui: diferente das outras duas features, abrir o
// promocode não busca nada no core — o único request é o POST do resgate, cuja
// falha já é tratada pela notificação.
export type PromocodeStatus = 'ready' | 'ineligible';

export interface PromocodeNotification {
  is_open: boolean;
  type: PromocodeNotificationType;
  title: string;
  message: string;
}

export const usePromocodeStore = defineStore('promocode', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  const playerProfileStore = usePlayerProfileStore();

  const isRedeeming = ref(false);

  /* ---------------- modal global ---------------- */

  // Abertura do modal (gatilho via store, sem navegar de rota): menu lateral,
  // card do hub, deep-link `?open=promocode` e a própria rota /promocode.
  const isOpen = ref(false);
  const status = ref<PromocodeStatus>('ready');

  // Código que entra no input já preenchido (deep-link `?promocode=XXX`, lido
  // pela página e pelo middleware do cupom). Vive na store, e não no componente,
  // porque quem conhece o código é quem MANDA abrir — o modal só o consome.
  const prefillCode = ref('');

  // Mesma regra da roletinha e do check-in: a promoção é de quem chegou ao nível
  // mínimo dela. Só o veredito FRESCO do core nega (`isKnownIneligibleFor`) —
  // num "não sei" (perfil em trânsito, requisição falhada, core offline, mapa
  // quebrado) o modal abre normal, porque negar o resgate por falta de resposta
  // tiraria o bônus de quem tem direito a ele. Quem dá a palavra final continua
  // sendo o core, no POST do resgate (`not_eligible`).
  const isKnownIneligible = computed(() =>
    playerProfileStore.isKnownIneligibleFor(GAMIFICATION_SLUGS.promocodes),
  );

  function open(options?: { code?: string }) {
    // Deslogado não abre o modal: só o login. O resgate exige auth de qualquer
    // forma, e o formulário por cima da tela de login só atrapalharia.
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    prefillCode.value = options?.code?.trim() ?? '';
    status.value = isKnownIneligible.value ? 'ineligible' : 'ready';
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  function reset() {
    status.value = 'ready';
    prefillCode.value = '';
  }

  const notification = ref<PromocodeNotification>({
    is_open: false,
    type: 'success',
    title: '',
    message: '',
  });

  const NOTIFICATION_TITLES: Record<PromocodeNotificationType, string> = {
    success: 'Código resgatado com sucesso',
    error: 'Não foi possível resgatar',
    warning: 'Promocode já resgatado',
  };

  // Códigos que são estado esperado do usuário (não falha) — exibidos
  // como aviso (amarelo) em vez de erro (vermelho).
  const WARNING_ERROR_CODES: PromocodeErrorCode[] = ['already_redeemed'];

  function getNotificationType(
    error: string | null,
  ): Exclude<PromocodeNotificationType, 'success'> {
    return isKnownErrorCode(error) && WARNING_ERROR_CODES.includes(error)
      ? 'warning'
      : 'error';
  }

  function openNotification(type: PromocodeNotificationType, message: string) {
    notification.value = {
      is_open: true,
      type,
      title: NOTIFICATION_TITLES[type],
      message,
    };
  }

  function closeNotification() {
    notification.value.is_open = false;
  }

  const UNAVAILABLE_MESSAGE =
    'Serviço indisponível no momento. Tente novamente.';

  const ERROR_MESSAGES: Record<PromocodeErrorCode, string> = {
    not_found: 'Promocode inválido.',
    inactive: 'Este promocode não está mais disponível.',
    not_started: 'Este promocode ainda não está disponível.',
    expired: 'Este promocode expirou.',
    out_of_stock: 'Este promocode atingiu o limite de usos.',
    already_redeemed: 'Você já resgatou este promocode.',
    not_eligible: 'Sua conta não é elegível para este promocode.',
    bonus_failed: 'Não foi possível aplicar o bônus. Tente novamente.',
  };

  function isKnownErrorCode(error: string | null): error is PromocodeErrorCode {
    return (
      error !== null &&
      Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, error)
    );
  }

  function getErrorMessage(
    status: number | null,
    error: string | null,
    message: string | null,
  ) {
    if (status === null) return UNAVAILABLE_MESSAGE;

    if (status === 429) {
      return 'Código esgotado';
    }

    // o core é a fonte de verdade do texto; o mapa local fica como fallback
    // pra quando a resposta não traz message (corpo ilegível, 200 fora do
    // contrato) ou pra um código novo ainda não mapeado.
    if (message?.trim()) return message.trim();

    if (isKnownErrorCode(error)) {
      return ERROR_MESSAGES[error];
    }

    return 'Promocode não aplicado.';
  }

  async function redeemPromocode(code: string): Promise<boolean> {
    // Enter no input ignora o :disabled do botão — sem este guard,
    // Enter repetido dispararia POSTs duplicados durante o resgate
    if (isRedeeming.value || !code.trim()) return false;

    if (!authStore.isAuthenticated) {
      openLogin();

      return false;
    }

    // Só o código vai no body: a identidade É O TOKEN. O core introspecta o
    // Bearer na Cactus (EnsureUserTokenIdentity) e resolve o jogador por ele —
    // `user_ext_id`, `user_email` e `user_cpf` saíram do RedeemRequest lá, e com
    // eles saíram as guardas locais de perfil carregado e CPF preenchido, que só
    // existiam pra alimentar esses campos.
    isRedeeming.value = true;

    try {
      const result = await redeemPromocodeHandler({
        code: code.trim().toUpperCase(),
      });

      if (result.ok) {
        openNotification('success', `Você recebeu: ${result.bonus.name}`);

        return true;
      }

      openNotification(
        getNotificationType(result.error),
        getErrorMessage(result.status, result.error, result.message),
      );

      const isExpectedBusinessError =
        result.status !== null &&
        result.status < 500 &&
        (result.status === 429 ||
          result.status === 422 ||
          isKnownErrorCode(result.error));

      if (!isExpectedBusinessError) {
        // erros de negócio mapeados são esperados e seriam ruído na triagem;
        // só rede/timeout, 5xx e códigos desconhecidos interessam ao Sentry
        Sentry.captureException(
          result.cause ??
            new Error(`promocode redeem failed (status ${result.status})`),
          {
            tags: { source: 'promocode.redeem' },
            extra: { status: result.status, errorCode: result.error },
          },
        );
      }

      return false;
    } finally {
      isRedeeming.value = false;
    }
  }

  return {
    isOpen,
    status,
    prefillCode,
    isRedeeming,
    notification,
    open,
    close,
    reset,
    redeemPromocode,
    openNotification,
    closeNotification,
  };
});
